import React, { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { library } from '@fortawesome/fontawesome-svg-core'
import { fas } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getRequest, postRequest, patchRequest, deleteRequest } from '@react/helpers/fetch'
import consumer from 'channels/consumer'
import Chat from '@shared/Chat'
import Sidebar from './Sidebar'

library.add(fas)

const McpChatApp = ({ tooltip = 'Assistant', pending = false }) => {
  const [isOpen, setIsOpen]               = useState(false)
  // Hidden rather than unmounted once built: the open conversation keeps its
  // subscription, so an answer still lands while out of sight.
  const [hasOpened, setHasOpened]         = useState(false)
  const [isGenerating, setIsGenerating]   = useState(pending)
  const [chats, setChats]                 = useState([])
  const [currentChatId, setCurrentChatId] = useState(null)
  const [sessionKey, setSessionKey]       = useState(null)

  const sessionKeyRef    = useRef(null)
  const wasGeneratingRef = useRef(pending)
  const loadIdRef        = useRef(0)
  sessionKeyRef.current = sessionKey

  // Only the last refresh asked for may set the list: a slower earlier answer
  // would put a finished run back to pending.
  const loadChats = useCallback(() => {
    const loadId = ++loadIdRef.current

    return getRequest('/mcp_chats.json').then(data => {
      const list = data.chats || []
      if (loadId === loadIdRef.current) setChats(list)
      return list
    })
  }, [])

  const openChat = useCallback(id => {
    setCurrentChatId(id)
    setSessionKey(`chat:${id}`)
  }, [])

  const startDraft = useCallback(() => {
    setCurrentChatId(null)
    setSessionKey(`draft:${Date.now()}`)
  }, [])

  const open = useCallback(() => {
    setHasOpened(true)
    setIsOpen(true)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    loadChats().then(list => {
      if (sessionKeyRef.current) return
      if (list[0]) openChat(list[0].id)
    })
  }, [isOpen, loadChats, openChat])

  const close = useCallback(() => setIsOpen(false), [])

  useEffect(() => {
    if (!isOpen) return undefined
    const onKey = e => { if (e.key === 'Escape') close() }
    document.body.classList.add('mcp-no-scroll')
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.classList.remove('mcp-no-scroll')
      document.removeEventListener('keydown', onKey)
    }
  }, [isOpen, close])

  const renameChat = useCallback((id, title) => {
    setChats(prev => prev.map(chat => (chat.id === id ? { ...chat, title } : chat)))
    patchRequest({ url: `/mcp_chats/${id}`, body: { title } }).then(loadChats)
  }, [loadChats])

  const deleteChat = useCallback(id => {
    deleteRequest(`/mcp_chats/${id}`).then(() => {
      setChats(prev => prev.filter(chat => chat.id !== id))
      if (currentChatId === id) {
        setCurrentChatId(null)
        setSessionKey(null)
      }
    })
  }, [currentChatId])

  const showChat = useCallback(() => (
    currentChatId
      ? getRequest(`/mcp_chats/${currentChatId}.json`)
      : Promise.resolve({ chat_id: null, messages: [] })
  ), [currentChatId])

  const onMessages = useCallback(body => {
    if (currentChatId) {
      return postRequest({ url: `/mcp_chats/${currentChatId}/messages`, body }).then(data => {
        loadChats()
        return data
      })
    }

    return postRequest({ url: '/mcp_chats' }).then(chat => {
      setCurrentChatId(chat.id)
      return postRequest({ url: `/mcp_chats/${chat.id}/messages`, body }).then(data => {
        loadChats()
        return data
      })
    })
  }, [currentChatId, loadChats])

  const onBusyChange = useCallback(busy => {
    setIsGenerating(busy)
    if (wasGeneratingRef.current && !busy) loadChats()
    wasGeneratingRef.current = busy
  }, [loadChats])

  // The open conversation reports the end of its own run; these would keep
  // their spinner until the list happens to be refreshed.
  const backgroundRuns = chats
    .filter(chat => chat.pending && chat.id !== currentChatId)
    .map(chat => chat.id)
    .join()

  useEffect(() => {
    if (!backgroundRuns) return undefined

    const subscriptions = backgroundRuns.split(',').map(id => (
      consumer.subscriptions.create(
        { channel: 'McpChatChannel', chat_id: Number(id) },
        {
          connected: () => loadChats(),
          received:  data => { if (data.done || data.error) loadChats() }
        }
      )
    ))

    return () => subscriptions.forEach(subscription => subscription.unsubscribe())
  }, [backgroundRuns, loadChats])

  const pendingElsewhere = chats.some(chat => chat.pending && chat.id !== currentChatId)
  const generatingAway   = (isGenerating || pendingElsewhere) && !isOpen
  const triggerTooltip   = generatingAway ? I18n.t('mcp_chat.generating') : tooltip

  const emptyState = (
    <>
      <div className="chat-empty-icon">
        <FontAwesomeIcon icon="comment-dots" />
      </div>
      {I18n.t('mcp_chat.empty_state')}
    </>
  )

  return (
    <>
      <button
        type="button"
        className={`faq-link mcp-trigger ${generatingAway ? 'is-generating' : ''}`}
        aria-label={triggerTooltip}
        onClick={open}
        {...{ tooltip: triggerTooltip, tooltip_position: 'bottom' }}
      >
        <FontAwesomeIcon icon="comment-dots" className="navbar__icon" />
        {generatingAway && <span className="mcp-trigger__pulse" />}
      </button>

      {hasOpened && createPortal(
        <div
          className={`mcp-overlay ${isOpen ? '' : 'mcp-overlay--hidden'}`}
          role="dialog"
          aria-modal="true"
          aria-hidden={!isOpen}
          aria-label="Assistant Yuman"
          onMouseDown={e => { if (e.target.classList.contains('mcp-overlay')) close() }}
        >
          <div className="mcp-window">
            <header className="mcp-toolbar">
              <div className="chat-heading">
                <span className="chat-heading__badge">
                  <FontAwesomeIcon icon="comment-dots" />
                </span>
                <div>
                  <div className="chat-heading__title">{I18n.t('mcp_chat.title')}</div>
                  <div className="chat-heading__subtitle">{I18n.t('mcp_chat.subtitle')}</div>
                </div>
              </div>
              <button
                type="button"
                className="mcp-toolbar__close"
                aria-label={I18n.t('mcp_chat.close')}
                onClick={close}
              >
                ×
              </button>
            </header>

            <div className="mcp-body">
              <Sidebar
                chats={chats}
                currentChatId={currentChatId}
                onNewChat={startDraft}
                onSelect={openChat}
                onRename={renameChat}
                onDelete={deleteChat}
              />

              <main className="mcp-main">
                <div className="mcp-conversation">
                  {sessionKey ? (
                    <Chat
                      key={sessionKey}
                      channel="McpChatChannel"
                      placeholder={I18n.t('mcp_chat.placeholder')}
                      allowImages={false}
                      onShow={showChat}
                      onMessages={onMessages}
                      onBusyChange={onBusyChange}
                      emptyState={emptyState}
                    />
                  ) : (
                    <div className="mcp-empty">
                      <FontAwesomeIcon icon="comment-dots" size="2x" />
                      <span>{I18n.t('mcp_chat.empty_state')}</span>
                    </div>
                  )}
                </div>
                <div className="mcp-disclaimer">{I18n.t('mcp_chat.disclaimer')}</div>
              </main>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

export default McpChatApp
